import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  Fragment,
  useMemo,
} from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Add,
  Delete,
  Save,
  ExpandLess,
  ExpandMore,
  UploadFile as UploadFileIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { callAPI } from "../../../functions/api";
import styles from "./genericDataEditor.module.css";
import { toast } from "react-toastify";
import { devError } from "../../../functions/logging";
import { to } from "mathjs";

const GenericJsonEditorTable = forwardRef(
  (
    {
      title = "Editable Table",
      fetchUrl,
      submitUrl = "static/upsert-batch",
      deleteUrl,
      keyValue,
      primaryKey = "Id",
      maxHeight = "400px",
      forceExpanded = false, // New prop to force expanded mode
      searchTerm = "",
      activeFilters = {},
      onSchemaUpdate,
      uploadedData = null, // New prop for uploaded data
    },
    ref
  ) => {
    const [rows, setRows] = useState([]);
    const [filteredRows, setFilteredRows] = useState([]);
    const [schema, setSchema] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(forceExpanded); // Initialize based on forceExpanded
    const [editingRows, setEditingRows] = useState(new Set()); // Track which rows are being edited
    const [isAddingRow, setIsAddingRow] = useState(false); // Track if we're adding a new row
    const fileInputRef = useRef(null);
    const tableContainerRef = useRef(null);
    const internalFields = ["Id", "LastUpdated"];
    const visibleSchema = useMemo(() => schema.filter((f) => f.display), [schema]);
    const fetchData = async () => {
      setLoading(true);
      try {
        if (keyValue) {
          fetchUrl = `${fetchUrl}&key=${keyValue}`;
        }
        const result = await callAPI(fetchUrl, "GET");
        setSchema(result.schema);
        const normalized = result.data.map((row) => {
          const mapped = {};
          result.schema.forEach((field) => {
            const match = Object.keys(row).find(
              (k) => k.toLowerCase() === field.name.toLowerCase()
            );
            mapped[field.name] = row[match];
          });
          return mapped;
        });
        setRows(normalized);

        // Notify parent component about schema update
        if (onSchemaUpdate && result.schema) {
          onSchemaUpdate(result.schema);
        }
      } catch (err) {
        console.error("Fetch failed", err);
      } finally {
        setLoading(false);
      }
    };

    // Filter and search logic
    useEffect(() => {
      let filtered = [...rows];

      // Apply search filter
      if (searchTerm && searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase().trim();
        filtered = filtered.filter((row) => {
          return visibleSchema.some((field) => {
            const value = row[field.name];
            if (value === null || value === undefined) return false;
            return String(value).toLowerCase().includes(searchLower);
          });
        });
      }

      // Apply column-specific filter
      if (activeFilters && activeFilters.column && activeFilters.value) {
        const filterValue = activeFilters.value.toLowerCase().trim();
        filtered = filtered.filter((row) => {
          const cellValue = row[activeFilters.column];
          if (cellValue === null || cellValue === undefined) return false;
          return String(cellValue).toLowerCase().includes(filterValue);
        });
      }

      setFilteredRows(filtered);
    }, [rows, searchTerm, activeFilters, visibleSchema]);

    useEffect(() => {
      if (fetchUrl) {
        if (forceExpanded) {
          // When forceExpanded is true, always fetch data immediately
          fetchData();
        } else if (expanded) {
          // Normal behavior: only fetch when expanded
          fetchData();
        }
      }
    }, [fetchUrl, expanded, forceExpanded]);

    const toggleExpand = () => {
      if (!forceExpanded) {
        setExpanded((prev) => !prev);
      }
    };

    const addRow = () => {
      const newRow = {};
      schema.forEach((field) => {
        newRow[field.name] = field.type === "Boolean" ? false : "";
      });

      const keyField = schema.find((f) => f.name.toLowerCase() === "key");
      if (keyField && keyValue !== undefined) {
        newRow["Key"] = keyValue;
      }

      setRows((prev) => {
        const newRows = [...prev, newRow];
        const newRowIndex = newRows.length - 1;

        // Set the new row as being edited and disable scrolling
        setEditingRows(new Set([newRowIndex]));
        setIsAddingRow(true);

        // Disable scrolling immediately when adding a new row
        setTimeout(() => {
          if (tableContainerRef.current) {
            tableContainerRef.current.style.overflowX = "hidden";
            tableContainerRef.current.style.overflowY = "hidden";
          }
        }, 0);

        return newRows;
      });
    };

    const handleInputChange = (index, field, value) => {
      const updated = [...rows];
      updated[index][field] = value;
      setRows(updated);
    };

    const startEditingRow = (index) => {
      // Only allow one row to be edited at a time
      setEditingRows(new Set([index]));
      // Disable scrolling on the table container when editing starts
      if (tableContainerRef.current) {
        tableContainerRef.current.style.overflowX = "hidden";
        tableContainerRef.current.style.overflowY = "hidden";
      }
    };

    const stopEditingRow = (index) => {
      setEditingRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
      if (isAddingRow && rows.length > 0) {
        setIsAddingRow(false);
      }
      // Re-enable scrolling when editing stops
      if (tableContainerRef.current) {
        tableContainerRef.current.style.overflowX = "auto";
        tableContainerRef.current.style.overflowY = "auto";
      }
    };

    const saveRow = (index) => {
      const row = rows[index];
      const error = validateRow(row);
      if (error) {
        toast.error(`Validation error: ${error}`);
        return;
      }
      stopEditingRow(index);
      toast.success("Row saved successfully");
    };

    const cancelEditRow = (index) => {
      if (isAddingRow && index === rows.length - 1) {
        // If cancelling a newly added row, remove it
        setRows((prev) => prev.slice(0, -1));
        setIsAddingRow(false);
      }
      stopEditingRow(index);
    };

    const validateRow = (row) => {
      const requiredFields = schema.filter(
        (f) => !f.isNullable && !internalFields.includes(f.name)
      );
      for (let field of requiredFields) {
        if (row[field.name] === undefined || row[field.name] === null || row[field.name] === "") {
          return `Field '${field.name}' is required.`;
        }
      }
      return null;
    };

    const handleSubmit = async () => {
      for (let row of rows) {
        const error = validateRow(row);
        if (error) return toast.error(`Validation error: ${error}`);
      }
      try {
        const response = await callAPI(submitUrl, "POST", rows, false);
        const result = await response.json();
        toast.success(`Submit successful: ${result.inserted} inserted, ${result.updated} updated`);
        fetchData();
      } catch (err) {
        toast.error("Submit failed: " + (err.message || "Unknown error"));
        devError("Submit error", err);
      }
    };

    const deleteRow = async (index) => {
      const row = rows[index];
      if (!window.confirm("Are you sure you want to delete this row?")) return;
      if (deleteUrl && row[primaryKey]) {
        try {
          await callAPI(`${deleteUrl}/${row[primaryKey]}`, "DELETE");
          await fetchData();
        } catch (err) {
          console.error("Delete failed:", err);
          devError("Delete error", err);
          toast.error("Failed to delete row.");
        }
      }
    };

    const deleteAllRows = async () => {
      if (!window.confirm("Are you sure you want to delete ALL rows?")) return;

      try {
        const ids = rows.map((r) => r[primaryKey]).filter(Boolean);
        if (deleteUrl && ids.length > 0) {
          await callAPI(`${deleteUrl}/delete-all`, "POST", ids);
          await fetchData();
        }
      } catch (err) {
        console.error("Delete all failed:", err);
        devError("Delete all error", err);
        toast.error("Failed to delete all rows.");
      }
    };

    const renderInputField = (field, value, onChange, isEditing = false) => {
      // If not editing, show read-only content
      if (!isEditing) {
        if (field.type === "Boolean") {
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "24px",
              }}
            >
              <Switch
                checked={!!value}
                disabled
                size="small"
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#7CAFC2",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#7CAFC2",
                  },
                  "& .MuiSwitch-track": {
                    backgroundColor: "#555",
                  },
                }}
              />
            </Box>
          );
        }

        return (
          <Box
            sx={{
              padding: "4px 8px",
              minHeight: "24px",
              display: "flex",
              alignItems: "center",
              width: "100%",
              minWidth: "fit-content",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: value ? "text.primary" : "text.secondary",
                fontSize: "0.875rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "200px",
              }}
            >
              {value || ""}
            </Typography>
          </Box>
        );
      }

      // Editing mode - show input fields
      if (field.type === "Boolean") {
        return (
          <FormControlLabel
            control={
              <Switch
                checked={!!value}
                onChange={(e) => onChange(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#7CAFC2", // muted sky blue or similar
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#7CAFC2",
                  },
                  "& .MuiSwitch-track": {
                    backgroundColor: "#555", // subtle off-state
                  },
                }}
              />
            }
            label="" // or you can add a label like "Enabled"
          />
        );
      }

      const inputMaxLength = field.maxLength ?? 12; // Ensure at least 12 chars for currency (e.g., 999,999.99)

      const inputProps = {
        maxLength: inputMaxLength,
        style: {
          minWidth: `${Math.min(inputMaxLength + 2, 10)}ch`,
          width: "auto",
        },
      };

      const InputProps = {
        disableUnderline: true,
        sx: {
          "&:before": {
            borderBottom: "none",
          },
          "&:after": {
            borderBottom: "none",
          },
          "& .MuiInputBase-input": {
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            borderRadius: "4px",
            padding: "8px 12px",
            fontSize: "0.875rem",
            color: "rgba(255, 255, 255, 0.9)",
            transition: "all 0.2s ease",
            "&:focus": {
              backgroundColor: "rgba(255, 255, 255, 0.08)",
              borderColor: "rgba(255, 255, 255, 0.4)",
              outline: "none",
            },
          },
        },
      };

      return (
        <TextField
          type={["Int32", "Double", "Decimal"].includes(field.type) ? "number" : "text"}
          variant="standard"
          value={value ?? ""}
          onChange={(e) => {
            const raw = e.target.value;
            let parsedValue = raw;

            if (["Decimal", "Double"].includes(field.type)) {
              parsedValue = parseFloat(raw);
            } else if (field.type === "Int32") {
              parsedValue = parseInt(raw, 10);
            }

            onChange(parsedValue);
          }}
          InputProps={InputProps}
          inputProps={inputProps}
          autoFocus
        />
      );
    };

    // Handle uploaded data from parent component
    useEffect(() => {
      if (uploadedData && uploadedData.length > 0) {
        // Add the uploaded data to existing rows
        setRows((prev) => {
          const newRows = [...prev, ...uploadedData];
          return newRows;
        });
      }
    }, [uploadedData]);

    // Cleanup: Re-enable table scrolling if component unmounts while editing
    useEffect(() => {
      return () => {
        if (tableContainerRef.current) {
          tableContainerRef.current.style.overflowX = "auto";
          tableContainerRef.current.style.overflowY = "auto";
        }
      };
    }, []);

    // Export function for parent component
    const exportToExcel = () => {
      const exportData = filteredRows.length > 0 ? filteredRows : rows;
      if (exportData.length === 0) {
        toast.warning("No data to export");
        return;
      }
      const sheet = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, sheet, "Data");
      XLSX.writeFile(
        wb,
        `${title.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      toast.success("Data exported successfully");
    };

    // Expose functions to parent component through ref
    useImperativeHandle(ref, () => ({
      addRow,
      deleteAllRows,
      handleSubmit,
      exportToExcel,
    }));

    return (
      <Paper elevation={1} className={expanded ? styles.containerExpanded : styles.container}>
        {!forceExpanded && (
          <Box className={styles.expander} onClick={toggleExpand}>
            <Box className={styles.titleBox}>
              <Typography className={styles.title}>{title.toUpperCase()}</Typography>
            </Box>
            <Box>
              <IconButton onClick={() => fileInputRef.current?.click()}>
                <UploadFileIcon />
              </IconButton>
              <IconButton
                onClick={() => {
                  const sheet = XLSX.utils.json_to_sheet(rows);
                  const wb = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(wb, sheet, "Data");
                  XLSX.writeFile(wb, `${title.replace(/\s+/g, "_")}.xlsx`);
                }}
              >
                <DownloadIcon />
              </IconButton>

              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept=".xlsx,.xls"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;

                  const data = await file.arrayBuffer();
                  const wb = XLSX.read(data);
                  const ws = wb.Sheets[wb.SheetNames[0]];
                  const json = XLSX.utils.sheet_to_json(ws);

                  // Force "Key" to uppercase and present even if keyValue is falsy
                  const updated = json.map((r) => ({
                    ...r,
                    Key: keyValue ?? "", // fallback to empty string if keyValue is undefined
                  }));

                  setRows((prev) => [...prev, ...updated]);
                }}
              />
            </Box>
            <IconButton>{expanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
          </Box>
        )}

        <Collapse in={expanded || forceExpanded}>
          {loading ? (
            <Box className={styles.loadingBox}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Results Counter */}
              {(searchTerm || (activeFilters && activeFilters.column)) && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 1,
                    bgcolor: "background.default",
                    borderRadius: 1,
                    mb: 1,
                    fontSize: "0.875rem",
                    color: "text.secondary",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Showing {filteredRows.length} of {rows.length} records
                  </Typography>
                  {(searchTerm || (activeFilters && activeFilters.column)) && (
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {searchTerm && `Search: "${searchTerm}"`}
                      {searchTerm && activeFilters && activeFilters.column && " | "}
                      {activeFilters &&
                        activeFilters.column &&
                        `Filter: ${activeFilters.column} = "${activeFilters.value}"`}
                    </Typography>
                  )}
                </Box>
              )}

              <TableContainer
                ref={tableContainerRef}
                className={styles.tableContainer}
                sx={{
                  height: "calc(100vh - 280px)", // Much more aggressive height to fill space
                  maxWidth: "100%",
                  overflowX: "auto",
                  overflowY: "auto",
                  bgcolor: "background.paper",
                  borderRadius: "8px",
                  border: "1px solid rgba(116, 175, 194, 0.2)",
                  "& .MuiTable-root": {
                    minWidth: "auto",
                    borderCollapse: "separate",
                    borderSpacing: 0,
                    tableLayout: "auto", // Allow table to auto-size columns
                    width: "100%",
                  },
                  "& .MuiTableHead-root": {
                    "& .MuiTableCell-root": {
                      bgcolor: "rgba(116, 175, 194, 0.1)",
                      borderBottom: "2px solid #74AFC2",
                      borderRight: "1px solid rgba(116, 175, 194, 0.15)",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: "#74AFC2",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      "&:last-child": {
                        borderRight: "none",
                      },
                    },
                  },
                  "& .MuiTableBody-root": {
                    "& .MuiTableRow-root": {
                      transition: "all 0.2s ease",
                      "& .MuiTableCell-root": {
                        borderBottom: "1px solid rgba(116, 175, 194, 0.1)",
                        borderRight: "1px solid rgba(116, 175, 194, 0.05)",
                        fontSize: "0.875rem",
                        color: "text.primary",
                        padding: "12px 16px",
                        "&:last-child": {
                          borderRight: "none",
                        },
                      },
                    },
                  },
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {visibleSchema.map((field) => (
                        <TableCell
                          key={field.name}
                          className={styles.tableHeaderCell}
                          sx={{ width: "auto", minWidth: "fit-content", whiteSpace: "nowrap" }}
                        >
                          {field.name}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={visibleSchema.length}
                          sx={{
                            textAlign: "center",
                            py: 4,
                            color: "text.secondary",
                            fontStyle: "italic",
                          }}
                        >
                          {rows.length === 0
                            ? "No data available"
                            : searchTerm || (activeFilters && activeFilters.column)
                              ? "No results found matching your search criteria"
                              : "No data to display"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRows.map((row, filteredIndex) => {
                        // Find the actual index in the original rows array
                        const actualIndex = rows.findIndex(
                          (r) => r[primaryKey] === row[primaryKey]
                        );
                        const isEditing = editingRows.has(actualIndex);

                        return (
                          <TableRow
                            key={row[primaryKey] || filteredIndex}
                            onClick={() => !isEditing && startEditingRow(actualIndex)}
                            sx={{
                              cursor: !isEditing ? "pointer" : "default",
                              backgroundColor: isEditing
                                ? "rgba(116, 175, 194, 0.1)"
                                : "transparent",
                              "&:hover": {
                                backgroundColor: !isEditing
                                  ? "rgba(200, 200, 200, 0.05)"
                                  : "rgba(116, 175, 194, 0.1)",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            {visibleSchema.map((field) => (
                              <TableCell
                                key={field.name}
                                sx={{ width: "auto", minWidth: "fit-content", padding: "8px 12px" }}
                              >
                                {renderInputField(
                                  field,
                                  row[field.name],
                                  () => {}, // No onChange for read-only
                                  false // Always read-only in table
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Collapse>

        {/* Floating Edit Overlay - Outside table container */}
        {editingRows.size > 0 &&
          (() => {
            const editingIndex = Array.from(editingRows)[0];
            const editingRow = rows[editingIndex];

            // Position overlay using fixed positioning relative to viewport
            const getRowPosition = () => {
              if (tableContainerRef.current) {
                // For new rows being added, position overlay at the top of the table
                if (isAddingRow && editingIndex === rows.length - 1) {
                  const containerRect = tableContainerRef.current.getBoundingClientRect();
                  return {
                    top: containerRect.top + 60, // Position below table header
                    left: containerRect.left,
                    width: containerRect.width,
                    height: 40,
                  };
                }

                // For existing rows, position overlay inline with the row
                const tableRows = tableContainerRef.current.querySelectorAll("tbody tr");
                const filteredRowIndex = filteredRows.findIndex(
                  (row) => rows.findIndex((r) => r[primaryKey] === row[primaryKey]) === editingIndex
                );
                const targetRow = tableRows[filteredRowIndex];
                if (targetRow) {
                  const rowRect = targetRow.getBoundingClientRect();
                  const containerRect = tableContainerRef.current.getBoundingClientRect();
                  return {
                    top: rowRect.top - 20, // Adjust position up by 20px total
                    left: containerRect.left,
                    width: containerRect.width,
                    height: rowRect.height,
                  };
                }
              }
              return { top: 100, left: 100, width: 600, height: 40 };
            };

            const rowPosition = getRowPosition();

            return (
              <Box
                sx={{
                  position: "fixed",
                  top: rowPosition.top,
                  left: rowPosition.left,
                  width: rowPosition.width,
                  zIndex: 1300,
                  backgroundColor: "rgba(25, 25, 25, 0.98)",
                  borderRadius: "8px",
                  padding: "12px 16px",
                  boxShadow:
                    "0 20px 60px rgba(0, 0, 0, 0.5), 0 8px 24px rgba(0, 0, 0, 0.4), 0 4px 12px rgba(0, 0, 0, 0.3)",
                  border: "1px solid rgba(255, 255, 255, 0.15)",
                  minHeight: "auto",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  transition: "all 0.2s ease",
                  backdropFilter: "blur(12px)",
                }}
              >
                {/* Form fields in a horizontal layout */}
                {visibleSchema.map((field, index) => (
                  <Box key={field.name} sx={{ flex: 1, minWidth: "80px" }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(255, 255, 255, 0.6)",
                        fontSize: "0.65rem",
                        mb: 0.3,
                        display: "block",
                        textTransform: "uppercase",
                        letterSpacing: "0.3px",
                        fontWeight: 500,
                      }}
                    >
                      {field.name}
                    </Typography>
                    {renderInputField(
                      field,
                      editingRow[field.name],
                      (val) => handleInputChange(editingIndex, field.name, val),
                      true
                    )}
                  </Box>
                ))}

                {/* Action buttons on the right */}
                <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0, ml: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => saveRow(editingIndex)}
                    sx={{
                      color: "rgba(255, 255, 255, 0.8)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        color: "rgba(255, 255, 255, 0.9)",
                      },
                      width: "24px",
                      height: "24px",
                    }}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => cancelEditRow(editingIndex)}
                    sx={{
                      color: "rgba(255, 255, 255, 0.6)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                        color: "rgba(255, 255, 255, 0.8)",
                      },
                      width: "24px",
                      height: "24px",
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => deleteRow(editingIndex)}
                    sx={{
                      color: "rgba(255, 100, 100, 0.8)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 100, 100, 0.1)",
                        color: "rgba(255, 100, 100, 0.9)",
                      },
                      width: "24px",
                      height: "24px",
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            );
          })()}
      </Paper>
    );
  }
);

export default GenericJsonEditorTable;
